<template>
  <div class="relative">
    <div class="constrain mt-16 mb-16 lg:mt-12">
      <div class="flex flex-col lg:flex-row gap-x-10 mt-4 lg:mt-12">
        <div class="w-full lg:w-1/2">
          <h3 class="heading-2">{{ blok.title }}</h3>
          <p v-if="blok.subtitle" class="mt-4">{{ blok.subtitle }}</p>
          <div class="mt-4 prose w-full max-w-full" v-html="resolvedRichText"></div>

        </div>
        <div
          class="w-full lg:w-1/2 divide-y divide-grey-1 space-y-5 mt-6 lg:mt-0"
          :class="{
            'lg:w-4/12 m-auto': uspsHaveBody(),
          }"
        >
          <div
            v-for="(usp, index) in blok.usps"
            class="flex space-x-3 items-start"
            :class="{ 'pt-4': index !== 0 }"
          >
            <template v-if="usp.icon && usp.icon.filename !== ''">
              <div
                class="w-[49px] h-[49px] flex-shrink-0 flex justify-center items-center text-primary bg-[#DCFBEA] rounded-lg text-center font-bold text-[1.3125rem]"
              >
                <NuxtImg
                  provider="storyblok"
                  :src="usp.icon.filename"
                  :alt="getImageAlt(usp.icon)"
                  loading="lazy"
                  format="webp"
                  width="32px"
                  height="32px"
                />
              </div>
            </template>
            <template v-else>
              <CommonSVGTemplate
                class="flex-shrink-0"
                width="32"
                height="32"
                alt="Arrow Icon"
                :path="arrowIcon"
                view-box="0 0 22 27"
              />
            </template>

            <div v-if="usp.body" class="flex flex-col">
              <p class="leading-tight font-bold mb-4 text-lg">
                {{ usp.name }}
              </p>
              <p class="text-grey-dark">
                {{ usp.body }}
              </p>
            </div>
            <div v-else>
              <p class="leading-tight">
                {{ usp.name }}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
    <Gradients :gradients="blok.gradients" />
  </div>
</template>
<script setup lang="ts">
const props = defineProps({ blok: Object });
const { getImageAlt } = useImageAlt();
const arrowIcon = `
<path fill-rule="evenodd" clip-rule="evenodd" d="M22 13.5C22 13.3266 21.8593 13.186 21.6857 13.186L0.314287 13.186C0.140705 13.186 1.32482e-06 13.3266 1.31724e-06 13.5C1.30967e-06 13.6734 0.140705 13.814 0.314287 13.814L21.6857 13.814C21.8593 13.814 22 13.6734 22 13.5ZM18.8571 11.3023C18.8571 11.1289 18.7164 10.9884 18.5429 10.9884L0.314287 10.9884C0.140705 10.9884 1.42089e-06 11.1289 1.41331e-06 11.3023C1.40573e-06 11.4757 0.140705 11.6163 0.314287 11.6163L18.5429 11.6163C18.7164 11.6163 18.8571 11.4757 18.8571 11.3023ZM15.7143 9.10465C15.7143 8.93127 15.5736 8.7907 15.4 8.7907L0.314287 8.7907C0.140705 8.7907 1.51695e-06 8.93127 1.50937e-06 9.10465C1.50179e-06 9.27803 0.140705 9.4186 0.314287 9.4186L15.4 9.41861C15.5736 9.41861 15.7143 9.27803 15.7143 9.10465ZM12.5714 6.90698C12.5714 6.7336 12.4307 6.59302 12.2571 6.59302L0.314287 6.59302C0.140705 6.59302 1.61301e-06 6.7336 1.60544e-06 6.90698C1.59786e-06 7.08036 0.140705 7.22093 0.314287 7.22093L12.2571 7.22093C12.4307 7.22093 12.5714 7.08036 12.5714 6.90698ZM9.42857 4.7093C9.42857 4.53592 9.28787 4.39535 9.11429 4.39535L0.314287 4.39535C0.140705 4.39535 1.70908e-06 4.53592 1.7015e-06 4.7093C1.69392e-06 4.88268 0.140705 5.02326 0.314287 5.02326L9.11429 5.02326C9.28787 5.02326 9.42857 4.88268 9.42857 4.7093ZM6.28571 2.51163C6.28572 2.33825 6.14501 2.19767 5.97143 2.19767L0.314287 2.19767C0.140705 2.19767 1.80514e-06 2.33825 1.79756e-06 2.51163C1.78998e-06 2.68501 0.140705 2.82558 0.314287 2.82558L5.97143 2.82558C6.14501 2.82558 6.28571 2.68501 6.28571 2.51163ZM2.82857 -8.3801e-07C3.00216 -8.30422e-07 3.14286 0.140573 3.14286 0.313953C3.14286 0.487332 3.00216 0.627906 2.82857 0.627906L0.314287 0.627906C0.140705 0.627906 1.88605e-06 0.487332 1.89363e-06 0.313953C1.9012e-06 0.140573 0.140705 -9.555e-07 0.314287 -9.47913e-07L2.82857 -8.3801e-07ZM2.82857 27C3.00215 27 3.14286 26.8594 3.14286 26.686C3.14286 26.5127 3.00215 26.3721 2.82857 26.3721L0.314286 26.3721C0.140704 26.3721 7.48443e-07 26.5127 7.40864e-07 26.686C7.33286e-07 26.8594 0.140704 27 0.314286 27L2.82857 27ZM5.97143 24.8023C6.14501 24.8023 6.28571 24.6618 6.28571 24.4884C6.28571 24.315 6.14501 24.1744 5.97143 24.1744L0.314286 24.1744C0.140704 24.1744 8.44506e-07 24.315 8.36928e-07 24.4884C8.29349e-07 24.6618 0.140704 24.8023 0.314286 24.8023L5.97143 24.8023ZM9.11429 22.6047C9.28787 22.6047 9.42857 22.4641 9.42857 22.2907C9.42857 22.1173 9.28787 21.9767 9.11429 21.9767L0.314286 21.9767C0.140704 21.9767 9.4057e-07 22.1173 9.32991e-07 22.2907C9.25413e-07 22.4641 0.140704 22.6047 0.314286 22.6047L9.11429 22.6047ZM12.2571 20.407C12.4307 20.407 12.5714 20.2664 12.5714 20.093C12.5714 19.9196 12.4307 19.7791 12.2571 19.7791L0.314286 19.7791C0.140704 19.7791 1.03663e-06 19.9196 1.02905e-06 20.093C1.02148e-06 20.2664 0.140704 20.407 0.314286 20.407L12.2571 20.407ZM15.4 18.2093C15.5736 18.2093 15.7143 18.0687 15.7143 17.8954C15.7143 17.722 15.5736 17.5814 15.4 17.5814L0.314286 17.5814C0.140704 17.5814 1.1327e-06 17.722 1.12512e-06 17.8954C1.11754e-06 18.0687 0.140704 18.2093 0.314286 18.2093L15.4 18.2093ZM18.5429 16.0116C18.7164 16.0116 18.8571 15.8711 18.8571 15.6977C18.8571 15.5243 18.7164 15.3837 18.5429 15.3837L0.314287 15.3837C0.140704 15.3837 1.22876e-06 15.5243 1.22118e-06 15.6977C1.2136e-06 15.8711 0.140704 16.0116 0.314286 16.0116L18.5429 16.0116Z" fill="#28C700"/>
`;

function uspsHaveBody() {
  return props.blok.usps.some((usp) => usp.body);
}

// Original computed property for visible content
const resolvedRichText = computed(() =>
  renderRichText(props.blok.body, {
    resolver: (component, blok) => {
      return `<component :blok='${JSON.stringify(
        blok
      )}' is="${component}"></component>`;
    },
  })
);
</script>
